'use client';

import { c as _c } from "react/compiler-runtime";
import { type ComponentProps, Suspense } from 'react';
import type { DateRange, DayPickerProps, Locale, Matcher, PropsRange } from 'react-day-picker';
import { addYears, isAfter, isBefore } from 'date-fns';
import dynamic from 'next/dynamic';
import { variantClasses } from '@/components/Typography';
import { DayPickerChevron } from '@/components/forms/fields/DateRangePicker/DayPickerChevron';
import { cn } from '@/lib/cn';
import './DateRangePicker.css';
const DayPicker = dynamic(() => import('react-day-picker').then(mod => mod.DayPicker), {
  ssr: false
});
const MonthGrid = dynamic(() => import('react-day-picker').then(mod => mod.MonthGrid), {
  ssr: false
});
const absMin = new Date();
const absMax = addYears(new Date(), 2);
const formatWeekdayName = (weekday: Date, options?: {
  locale?: Locale;
}) => weekday.toLocaleDateString(options?.locale?.code ?? 'en-AU', {
  weekday: 'short'
}).charAt(0);
const modifiersClassNames = {
  disabledMinMax: 'rdp-disabled-minmax',
  disabledClosed: 'rdp-disabled-closed'
};
export interface DateRangePickerProps extends Omit<DayPickerProps, 'components' | 'formatters' | 'startMonth' | 'endMonth' | 'disabled' | 'modifiers' | 'modifiersClassNames' | 'showOutsideDays' | 'navLayout' | 'animate' | 'className' | 'mode' | 'selected' | 'onSelect'> {
  value?: DateRange;
  onChange?: PropsRange['onSelect'];
  minDate?: Date;
  maxDate?: Date;
  closedDates?: Matcher | Matcher[] | undefined;
}
function getMinDate(minDate: Date | undefined) {
  if (!minDate || isBefore(minDate, absMin)) {
    return absMin;
  }
  return minDate;
}
function getMaxDate(maxDate: Date | undefined) {
  if (!maxDate || isAfter(maxDate, absMax)) {
    return absMax;
  }
  return maxDate;
}
export function DateRangePicker(t0) {
  const $ = _c(48);
  let closedDates;
  let inMaxDate;
  let inMinDate;
  let onChange;
  let props;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      onChange,
      minDate: inMinDate,
      maxDate: inMaxDate,
      closedDates,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = closedDates;
    $[2] = inMaxDate;
    $[3] = inMinDate;
    $[4] = onChange;
    $[5] = props;
    $[6] = value;
  } else {
    closedDates = $[1];
    inMaxDate = $[2];
    inMinDate = $[3];
    onChange = $[4];
    props = $[5];
    value = $[6];
  }
  let t1;
  if ($[7] !== inMinDate) {
    t1 = getMinDate(inMinDate);
    $[7] = inMinDate;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const minDate = t1;
  let t2;
  if ($[9] !== inMaxDate) {
    t2 = getMaxDate(inMaxDate);
    $[9] = inMaxDate;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const maxDate = t2;
  let t3;
  if ($[11] !== closedDates) {
    t3 = closedDates && !Array.isArray(closedDates) ? [closedDates] : closedDates;
    $[11] = closedDates;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const disabledClosed = t3;
  const numberOfMonths = props.numberOfMonths || 1;
  const t4 = numberOfMonths > 1;
  let t5;
  if ($[13] !== t4) {
    t5 = cn("h-75.25", {
      "w-172": t4
    });
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t5) {
    t6 = <div className={t5}>loading</div>;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      Chevron: DayPickerChevron,
      MonthGrid: ScrollableMonthGrid
    };
    t8 = {
      formatWeekdayName
    };
    $[17] = t7;
    $[18] = t8;
  } else {
    t7 = $[17];
    t8 = $[18];
  }
  let t9;
  if ($[19] !== disabledClosed) {
    t9 = disabledClosed ? disabledClosed : [];
    $[19] = disabledClosed;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== minDate) {
    t10 = {
      before: minDate
    };
    $[21] = minDate;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== maxDate) {
    t11 = {
      after: maxDate
    };
    $[23] = maxDate;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t9) {
    t12 = [...t9, t10, t11];
    $[25] = t10;
    $[26] = t11;
    $[27] = t9;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== maxDate || $[30] !== minDate) {
    t13 = {
      before: minDate,
      after: maxDate
    };
    $[29] = maxDate;
    $[30] = minDate;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== disabledClosed || $[33] !== t13) {
    t14 = {
      disabledMinMax: t13,
      disabledClosed
    };
    $[32] = disabledClosed;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn("mx-auto", variantClasses["paragraph-heavy"]);
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== maxDate || $[37] !== minDate || $[38] !== numberOfMonths || $[39] !== onChange || $[40] !== props || $[41] !== t12 || $[42] !== t14 || $[43] !== value) {
    t16 = <DayPicker components={t7} formatters={t8} startMonth={minDate} endMonth={maxDate} disabled={t12} modifiers={t14} modifiersClassNames={modifiersClassNames} showOutsideDays={false} navLayout="around" animate={true} className={t15} mode="range" selected={value} onSelect={onChange} {...props} numberOfMonths={numberOfMonths} />;
    $[36] = maxDate;
    $[37] = minDate;
    $[38] = numberOfMonths;
    $[39] = onChange;
    $[40] = props;
    $[41] = t12;
    $[42] = t14;
    $[43] = value;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t16 || $[46] !== t6) {
    t17 = <Suspense fallback={t6}>{t16}</Suspense>;
    $[45] = t16;
    $[46] = t6;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  return t17;
}
function ScrollableMonthGrid(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <div className="lg:max-h-64 overflow-x-hidden overflow-y-auto scrollbar-primary scrollbar-thin"><MonthGrid {...props} /></div>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}